import React, { useEffect, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Box } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './ArtifactLayout.module.css';

interface ArtifactLayoutProps {
  children: React.ReactNode;
  artifactContent: React.ReactNode;
  isArtifactOpen: boolean;
  onArtifactClose: () => void;
  defaultSizes?: [number, number];
  minSizes?: [number, number];
}

const ArtifactLayout: React.FC<ArtifactLayoutProps> = ({
  children,
  artifactContent,
  isArtifactOpen,
  onArtifactClose,
  defaultSizes = [50, 50],
  minSizes = [30, 30],
}) => {
  const [panelSizes, setPanelSizes] = useState<number[]>(defaultSizes);

  // Save panel sizes to localStorage
  const handleLayout = (sizes: number[]) => {
    setPanelSizes(sizes);
    localStorage.setItem('artifact-panel-sizes', JSON.stringify(sizes));
    try {
      window.dispatchEvent(new CustomEvent('artifact-panel-resized', { detail: { sizes } }));
    } catch {}
  };

  // Restore panel sizes from localStorage
  useEffect(() => {
    const savedSizes = localStorage.getItem('artifact-panel-sizes');
    if (savedSizes) {
      try {
        const sizes = JSON.parse(savedSizes);
        if (Array.isArray(sizes) && sizes.length === 2) {
          setPanelSizes(sizes);
          try {
            window.dispatchEvent(new CustomEvent('artifact-panel-resized', { detail: { sizes } }));
          } catch {}
        }
      } catch (e) {
        console.error('Failed to parse saved panel sizes:', e);
      }
    }
  }, []);

  // Handle keyboard shortcut for closing
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Escape to close artifact panel
      if (e.key === 'Escape' && isArtifactOpen) {
        onArtifactClose();
      }
    };

    if (isArtifactOpen) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [isArtifactOpen, onArtifactClose]);

  // Always use split screen when open
  if (isArtifactOpen) {
    return (
      <PanelGroup
        direction="horizontal"
        onLayout={handleLayout}
        autoSaveId="artifact-layout"
        className={styles.panelGroup}
      >
        <Panel
          defaultSize={panelSizes[0]}
          minSize={minSizes[0]}
          className={styles.chatPanel}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key="chat-content"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ height: '100%', width: '100%' }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </Panel>

        <PanelResizeHandle className={styles.resizeHandle}>
          <div className={styles.resizeHandleInner}>
            <div className={styles.resizeHandleLine} />
            <div className={styles.resizeHandleGrip}>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className={styles.resizeHandleLine} />
          </div>
        </PanelResizeHandle>

        <Panel
          defaultSize={panelSizes[1]}
          minSize={minSizes[1]}
          className={styles.artifactPanel}
        >
          <AnimatePresence>
            <motion.div
              key="artifact-content"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 20,
              }}
              style={{ height: '100%', width: '100%' }}
            >
              {artifactContent}
            </motion.div>
          </AnimatePresence>
        </Panel>
      </PanelGroup>
    );
  }

  // When artifact is closed, just render children normally
  return <>{children}</>;
};

export default ArtifactLayout;
